export type LotIdentityCandidate = string | number | null | undefined

export type LegacyLotIdentitySource = 'lotId' | 'canonicalLotKey' | 'lot' | 'rowId' | 'unknown'

export type LegacyLotIdentityResolution = {
  token: string
  source: LegacyLotIdentitySource
}

export type AvailableLotMatch = {
  id: string
  lotKey: string
  stage?: string
}

export type AvailableLotMatchResult =
  | { status: 'matched'; match: AvailableLotMatch }
  | { status: 'ambiguous'; matches: AvailableLotMatch[] }
  | { status: 'unmatched' }

type LegacyLotIdentityCandidate = {
  source: Exclude<LegacyLotIdentitySource, 'unknown'>
  value: LotIdentityCandidate
}

type LotRosterLike = {
  id?: LotIdentityCandidate
  lotKey?: LotIdentityCandidate
  stage?: LotIdentityCandidate
}

const UNKNOWN_LOT_TOKEN = 'unknown-lot'

const isLotRosterLike = (value: unknown): value is LotRosterLike => (
  typeof value === 'object' && value !== null
)

const canonicalizeLabel = (value: string | null | undefined): string => {
  if (!value) return ''
  return value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

const normalizeLotKeyValue = (value: LotIdentityCandidate): string | undefined => {
  if (value === null || value === undefined) return undefined
  const raw = String(value).trim()
  if (raw.length === 0) return undefined

  const canonical = canonicalizeLabel(raw)
  if (!canonical) return undefined

  if (/^\d+$/.test(canonical)) {
    return canonical
  }

  const lotPrefixPattern = /^(?:lote-de|lote|lot)-?\d+$/
  if (lotPrefixPattern.test(canonical)) {
    return canonical.replace(/^(?:lote-de|lote|lot)-?/, '')
  }

  const lotCompoundPattern = /^(?:lote-de|lote|lot)-\d+-\d+$/
  if (lotCompoundPattern.test(canonical)) {
    return canonical.replace(/^(?:lote-de|lote|lot)-/, '')
  }

  return canonical
}

export const resolveCanonicalLotKey = (...values: LotIdentityCandidate[]): string => {
  for (const value of values) {
    const normalized = normalizeLotKeyValue(value)
    if (normalized) return normalized
  }
  return UNKNOWN_LOT_TOKEN
}

export const resolveLegacyLotIdentity = (
  ...candidates: LegacyLotIdentityCandidate[]
): LegacyLotIdentityResolution => {
  for (const candidate of candidates) {
    const normalized = normalizeLotKeyValue(candidate.value)
    if (normalized) {
      return {
        token: normalized,
        source: candidate.source
      }
    }
  }

  return {
    token: UNKNOWN_LOT_TOKEN,
    source: 'unknown'
  }
}

const normalizeAvailableLotMatch = (value: unknown): (AvailableLotMatch & { token: string }) | null => {
  if (!isLotRosterLike(value)) return null

  const id = String(value.id ?? '').trim()
  const lotKey = String(value.lotKey ?? '').trim()
  if (!id || !lotKey) return null

  const token = normalizeLotKeyValue(value.lotKey)
  if (!token) return null

  const stage = typeof value.stage === 'string' ? value.stage : undefined

  return { id, lotKey, token, stage }
}

export const findAvailableLotMatch = (
  incomingLotLabel: LotIdentityCandidate,
  availableLots: ReadonlyArray<unknown>
): AvailableLotMatchResult => {
  const rawIncomingLotLabel = String(incomingLotLabel ?? '').trim()
  if (!rawIncomingLotLabel) {
    return { status: 'unmatched' }
  }

  const incomingToken = normalizeLotKeyValue(incomingLotLabel)
  if (!incomingToken) {
    return { status: 'unmatched' }
  }

  const exactMatches: AvailableLotMatch[] = []
  const canonicalMatches: AvailableLotMatch[] = []

  for (const lot of availableLots) {
    const normalized = normalizeAvailableLotMatch(lot)
    if (!normalized) continue

    const match = { id: normalized.id, lotKey: normalized.lotKey, stage: normalized.stage }
    if (normalized.lotKey === rawIncomingLotLabel) {
      exactMatches.push(match)
    }
    if (normalized.token === incomingToken) {
      canonicalMatches.push(match)
    }
  }

  if (exactMatches.length === 1) {
    return { status: 'matched', match: exactMatches[0] }
  }

  if (exactMatches.length > 1) {
    return { status: 'ambiguous', matches: exactMatches }
  }

  if (canonicalMatches.length === 1) {
    return { status: 'matched', match: canonicalMatches[0] }
  }

  if (canonicalMatches.length > 1) {
    return { status: 'ambiguous', matches: canonicalMatches }
  }

  return { status: 'unmatched' }
}

export const canonicalizeLotToken = (value: LotIdentityCandidate): string =>
  (value ?? '').toString().toLowerCase().replace(/[^a-z0-9]/g, '')